import type { Action, Reducer } from 'redux';
import { IApplicationState, IAppThunkAction } from '.';
import { IPaymentShare, ISplitPaymentShareRequest } from '../interfaces/ISplitPayment';
import ISplitAllocateInfo from '../interfaces/ISplitAllocateInfo';
import SplitPaymentService from '../service/splitPayments';
import { appInsights } from '../Logging/appInsights';

export interface ISplitAllocateState {
    shareToken: string | undefined;
    info: ISplitAllocateInfo | undefined;
    loading: boolean;
    error: boolean;
    errorType: 'notfound' | 'generic' | undefined;
    submitLoading: boolean;
    submitCreated: boolean;
    submitShares: IPaymentShare[];
    mutatingShareToken: string | undefined;
    mutateError: boolean;
}

const FETCH_ALLOCATE_INFO_REQUESTED: 'FETCH_ALLOCATE_INFO_REQUESTED' = 'FETCH_ALLOCATE_INFO_REQUESTED';
const FETCH_ALLOCATE_INFO_SUCCESS: 'FETCH_ALLOCATE_INFO_SUCCESS' = 'FETCH_ALLOCATE_INFO_SUCCESS';
const FETCH_ALLOCATE_INFO_FAILED: 'FETCH_ALLOCATE_INFO_FAILED' = 'FETCH_ALLOCATE_INFO_FAILED';
const SUBMIT_SHARES_REQUESTED: 'SUBMIT_SHARES_REQUESTED' = 'SUBMIT_SHARES_REQUESTED';
const SUBMIT_SHARES_SUCCESS: 'SUBMIT_SHARES_SUCCESS' = 'SUBMIT_SHARES_SUCCESS';
const SUBMIT_SHARES_FAILED: 'SUBMIT_SHARES_FAILED' = 'SUBMIT_SHARES_FAILED';
const MUTATE_SHARE_REQUESTED: 'MUTATE_SHARE_REQUESTED' = 'MUTATE_SHARE_REQUESTED';
const MUTATE_SHARE_SUCCESS: 'MUTATE_SHARE_SUCCESS' = 'MUTATE_SHARE_SUCCESS';
const MUTATE_SHARE_FAILED: 'MUTATE_SHARE_FAILED' = 'MUTATE_SHARE_FAILED';

interface IFetchAllocateInfoRequestedAction extends Action {
    type: typeof FETCH_ALLOCATE_INFO_REQUESTED;
    shareToken: string;
}

interface IFetchAllocateInfoSuccessAction extends Action {
    type: typeof FETCH_ALLOCATE_INFO_SUCCESS;
    info: ISplitAllocateInfo;
}

interface IFetchAllocateInfoFailedAction extends Action {
    type: typeof FETCH_ALLOCATE_INFO_FAILED;
    errorType: 'notfound' | 'generic';
}

interface ISubmitSharesRequestedAction extends Action {
    type: typeof SUBMIT_SHARES_REQUESTED;
}

interface ISubmitSharesSuccessAction extends Action {
    type: typeof SUBMIT_SHARES_SUCCESS;
    shares: IPaymentShare[];
}

interface ISubmitSharesFailedAction extends Action {
    type: typeof SUBMIT_SHARES_FAILED;
}

interface IMutateShareRequestedAction extends Action {
    type: typeof MUTATE_SHARE_REQUESTED;
    shareToken: string;
}

interface IMutateShareSuccessAction extends Action {
    type: typeof MUTATE_SHARE_SUCCESS;
}

interface IMutateShareFailedAction extends Action {
    type: typeof MUTATE_SHARE_FAILED;
}

type KnownSplitAllocateActions =
    | IFetchAllocateInfoRequestedAction
    | IFetchAllocateInfoSuccessAction
    | IFetchAllocateInfoFailedAction
    | ISubmitSharesRequestedAction
    | ISubmitSharesSuccessAction
    | ISubmitSharesFailedAction
    | IMutateShareRequestedAction
    | IMutateShareSuccessAction
    | IMutateShareFailedAction;

export const actionCreators = {
    fetchAllocateInfo:
        (shareToken: string): IAppThunkAction<KnownSplitAllocateActions> =>
        (dispatch) => {
            dispatch({ type: FETCH_ALLOCATE_INFO_REQUESTED, shareToken });

            SplitPaymentService.GetAllocateInfo(shareToken)
                .then((data: ISplitAllocateInfo) => {
                    dispatch({ type: FETCH_ALLOCATE_INFO_SUCCESS, info: data });
                })
                .catch((error: Error) => {
                    const errorType = error.name?.includes('404') ? 'notfound' : 'generic';
                    dispatch({ type: FETCH_ALLOCATE_INFO_FAILED, errorType });
                    appInsights?.trackException(
                        { error: new Error('Error fetching split allocate info'), severityLevel: 2 },
                        { exception: error, sourceFile: 'SplitAllocate.ts', product: 'booking' }
                    );
                });
        },

    submitShares:
        (shares: ISplitPaymentShareRequest[], deadlineHours?: number): IAppThunkAction<KnownSplitAllocateActions> =>
        (dispatch, getState) => {
            const appState: IApplicationState = getState();
            const info = appState.splitAllocate.info;
            const shareToken = appState.splitAllocate.shareToken;

            if (!info) return;

            const { existingShares } = info;
            const organizerShare = existingShares.find((s) => s.isOrganizer);
            const organizerSlotCount = organizerShare?.slotCount ?? 1;

            if (!shareToken) return;

            dispatch({ type: SUBMIT_SHARES_REQUESTED });

            const request = {
                shares,
                deadlineHours,
                organizerSlotCount,
            };

            const apiCall = SplitPaymentService.AddShares(shareToken, request);

            apiCall
                .then((data) => {
                    dispatch({ type: SUBMIT_SHARES_SUCCESS, shares: data });

                    // Re-fetch to refresh the full shares list
                    const shareToken = getState().splitAllocate.shareToken;
                    if (!shareToken) return;
                    SplitPaymentService.GetAllocateInfo(shareToken)
                        .then((refreshedInfo: ISplitAllocateInfo) => {
                            dispatch({ type: FETCH_ALLOCATE_INFO_SUCCESS, info: refreshedInfo });
                        })
                        .catch((error: Error) => {
                            appInsights?.trackException(
                                { error: new Error('Error refreshing allocate info after submit'), severityLevel: 3 },
                                { exception: error, sourceFile: 'SplitAllocate.ts', product: 'booking' }
                            );
                        });
                })
                .catch((error: Error) => {
                    dispatch({ type: SUBMIT_SHARES_FAILED });
                    appInsights?.trackException(
                        { error: new Error('Error submitting split payment shares'), severityLevel: 2 },
                        { exception: error, sourceFile: 'SplitAllocate.ts', product: 'booking' }
                    );
                });
        },

    // Organiser changes an invited player's slot count. Reuses the same endpoint customers use on
    // their own share (keyed by that player's share token), then refreshes the full allocate list.
    updateShareAllocation:
        (participantShareToken: string, slotCount: number): IAppThunkAction<KnownSplitAllocateActions> =>
        (dispatch, getState) => {
            dispatch({ type: MUTATE_SHARE_REQUESTED, shareToken: participantShareToken });

            SplitPaymentService.UpdateShareAllocation(participantShareToken, slotCount)
                .then(() => {
                    dispatch({ type: MUTATE_SHARE_SUCCESS });
                    refreshAllocateInfo(dispatch, getState().splitAllocate.shareToken);
                })
                .catch((error: Error) => {
                    dispatch({ type: MUTATE_SHARE_FAILED });
                    appInsights?.trackException(
                        { error: new Error('Error updating split payment share allocation'), severityLevel: 2 },
                        { exception: error, sourceFile: 'SplitAllocate.ts', product: 'booking' }
                    );
                });
        },

    // Organiser removes an invited player. Reuses the customer "decline" endpoint (keyed by that
    // player's share token); the released slots become unallocated again after the refresh.
    removeShare:
        (participantShareToken: string): IAppThunkAction<KnownSplitAllocateActions> =>
        (dispatch, getState) => {
            dispatch({ type: MUTATE_SHARE_REQUESTED, shareToken: participantShareToken });

            SplitPaymentService.DeclineShare(participantShareToken)
                .then(() => {
                    dispatch({ type: MUTATE_SHARE_SUCCESS });
                    refreshAllocateInfo(dispatch, getState().splitAllocate.shareToken);
                })
                .catch((error: Error) => {
                    dispatch({ type: MUTATE_SHARE_FAILED });
                    appInsights?.trackException(
                        { error: new Error('Error removing split payment share'), severityLevel: 2 },
                        { exception: error, sourceFile: 'SplitAllocate.ts', product: 'booking' }
                    );
                });
        },
};

// Re-fetches the organiser allocate info and pushes it into state. Used after a share mutation so the
// list, allocated/unallocated counts and per-share status all reflect the change.
function refreshAllocateInfo(dispatch: (action: KnownSplitAllocateActions) => void, shareToken: string | undefined) {
    if (!shareToken) return;
    SplitPaymentService.GetAllocateInfo(shareToken)
        .then((refreshedInfo: ISplitAllocateInfo) => {
            dispatch({ type: FETCH_ALLOCATE_INFO_SUCCESS, info: refreshedInfo });
        })
        .catch((error: Error) => {
            appInsights?.trackException(
                { error: new Error('Error refreshing allocate info after share mutation'), severityLevel: 3 },
                { exception: error, sourceFile: 'SplitAllocate.ts', product: 'booking' }
            );
        });
}

const unloadedState: ISplitAllocateState = {
    shareToken: undefined,
    info: undefined,
    loading: false,
    error: false,
    errorType: undefined,
    submitLoading: false,
    submitCreated: false,
    submitShares: [],
    mutatingShareToken: undefined,
    mutateError: false,
};

export const reducer: Reducer<ISplitAllocateState> = (
    state: ISplitAllocateState | undefined,
    incomingAction: Action
): ISplitAllocateState => {
    if (state === undefined) return unloadedState;

    const action = incomingAction as KnownSplitAllocateActions;
    switch (action.type) {
        case FETCH_ALLOCATE_INFO_REQUESTED:
            return {
                ...state,
                shareToken: action.shareToken,
                loading: true,
                error: false,
                errorType: undefined,
            };
        case FETCH_ALLOCATE_INFO_SUCCESS:
            return {
                ...state,
                loading: false,
                info: action.info,
                error: false,
                errorType: undefined,
            };
        case FETCH_ALLOCATE_INFO_FAILED:
            return {
                ...state,
                loading: false,
                error: true,
                errorType: action.errorType,
            };
        case SUBMIT_SHARES_REQUESTED:
            return {
                ...state,
                submitLoading: true,
                submitCreated: false,
            };
        case SUBMIT_SHARES_SUCCESS:
            return {
                ...state,
                submitLoading: false,
                submitCreated: true,
                submitShares: action.shares,
            };
        case SUBMIT_SHARES_FAILED:
            return {
                ...state,
                submitLoading: false,
            };
        case MUTATE_SHARE_REQUESTED:
            return {
                ...state,
                mutatingShareToken: action.shareToken,
                mutateError: false,
            };
        case MUTATE_SHARE_SUCCESS:
            return {
                ...state,
                mutatingShareToken: undefined,
                mutateError: false,
                // Changing/removing an allocation invalidates the "shares just created" success
                // state, so the add-players form is restored once freed slots reopen the panel.
                submitCreated: false,
                submitShares: [],
            };
        case MUTATE_SHARE_FAILED:
            return {
                ...state,
                mutatingShareToken: undefined,
                mutateError: true,
            };
        default:
            return state;
    }
};
